import { summarizeCurrencyAmounts } from "./reporting.js";

// Pure computations behind /admin/analytics. Revenue is always kept per
// currency (INR, USD and CAD are never summed together).

// Cart checkouts store payments.course_id = NULL, so a payment's course (and
// ISO standard) is often unknown. Such revenue is attributed here instead of
// being dropped.
export const UNKNOWN_COURSE_BUCKET = "Multiple/unknown";

const round2 = (value) => Math.round(value * 100) / 100;

const divideTotals = (totals, divisor) =>
  Object.fromEntries(
    Object.entries(totals).map(([currency, amount]) => [
      currency,
      divisor > 0 ? round2(amount / divisor) : 0,
    ]),
  );

const time = (value) => (value ? new Date(value).getTime() : NaN);
const paymentDate = (payment) => payment.paid_at || payment.created_at;

// Percentage change per currency between two equal-length periods. A currency
// with no prior revenue has no meaningful growth rate, so it is null.
export function revenueGrowthByCurrency(current, prior) {
  const currencies = new Set([...Object.keys(current), ...Object.keys(prior)]);
  return Object.fromEntries(
    [...currencies].map((currency) => {
      const before = prior[currency] || 0;
      const now = current[currency] || 0;
      return [
        currency,
        before > 0 ? Math.round(((now - before) / before) * 100) : null,
      ];
    }),
  );
}

export function formatGrowthByCurrency(growth = {}) {
  const entries = Object.entries(growth).sort(([a], [b]) => a.localeCompare(b));
  if (!entries.length) return "No revenue in either period";
  return entries
    .map(([currency, pct]) =>
      pct === null
        ? `${currency}: no prior revenue`
        : `${currency}: ${pct > 0 ? "+" : ""}${pct}%`,
    )
    .join(" · ");
}

// Month-over-month change for a series of values; null where the previous
// month is zero (no defined rate).
export function monthOverMonthGrowth(values) {
  return values.map((value, index) => {
    if (index === 0) return null;
    const previous = values[index - 1];
    return previous > 0 ? ((value - previous) / previous) * 100 : null;
  });
}

export function averageDefined(values) {
  const defined = values.filter(
    (value) => value !== null && Number.isFinite(value),
  );
  return defined.length
    ? defined.reduce((sum, value) => sum + value, 0) / defined.length
    : null;
}

export function trendDirection(values) {
  if (values.length < 2) return null;
  const [previous, latest] = values.slice(-2);
  if (latest > previous) return "up";
  if (latest < previous) return "down";
  return "flat";
}

// The analytics window is [start, now]; revenue growth compares it with the
// equally long window immediately before it ([priorStart, start)).
export function summarizeAdminAnalytics({
  users = [],
  enrollments = [],
  payments = [],
  progress = [],
  start,
  priorStart,
  now = new Date(),
}) {
  const startMs = time(start);
  const priorStartMs = time(priorStart);
  const nowMs = time(now);
  const completedPayments = payments.filter(
    (payment) => payment.payment_status === "completed",
  );
  const current = completedPayments.filter((payment) => {
    const at = time(paymentDate(payment));
    return at >= startMs && at <= nowMs;
  });
  const prior = completedPayments.filter((payment) => {
    const at = time(paymentDate(payment));
    return at >= priorStartMs && at < startMs;
  });
  const windowEnrollments = enrollments.filter(
    (row) => time(row.enrolled_at) >= startMs,
  );
  const windowProgress = progress.filter(
    (row) => time(row.last_accessed_at) >= startMs,
  );

  const activeUserIds = new Set(
    [
      ...windowEnrollments.map((row) => row.user_id),
      ...windowProgress.map((row) => row.user_id),
    ].filter(Boolean),
  );
  const revenue = summarizeCurrencyAmounts(current);
  const completed = windowEnrollments.filter(
    (row) => row.enrollment_status === "completed",
  ).length;
  const totalMinutes = windowProgress.reduce(
    (sum, row) => sum + Number(row.time_spent_minutes || 0),
    0,
  );

  const certifications = new Map();
  const bucket = (standard) => {
    if (!certifications.has(standard)) {
      certifications.set(standard, {
        enrollments: 0,
        completed: 0,
        payments: [],
      });
    }
    return certifications.get(standard);
  };
  for (const row of windowEnrollments) {
    const standard = row.courses?.iso_standard;
    if (!standard) continue;
    const stats = bucket(standard);
    stats.enrollments += 1;
    if (row.enrollment_status === "completed") stats.completed += 1;
  }
  for (const payment of current) {
    bucket(payment.course?.iso_standard || UNKNOWN_COURSE_BUCKET).payments.push(
      payment,
    );
  }

  const methods = new Map();
  for (const payment of current) {
    const method = payment.payment_method || "Unknown";
    if (!methods.has(method)) methods.set(method, []);
    methods.get(method).push(payment);
  }

  const countryOf = new Map(
    users.map((user) => [user.id, user.country?.trim() || "Unknown"]),
  );
  const regions = new Map();
  const region = (name) => {
    if (!regions.has(name))
      regions.set(name, { users: new Set(), payments: [] });
    return regions.get(name);
  };
  for (const userId of activeUserIds)
    region(countryOf.get(userId) || "Unknown").users.add(userId);
  for (const payment of current)
    region(countryOf.get(payment.user_id) || "Unknown").payments.push(payment);

  return {
    total_users: users.length,
    active_users: activeUserIds.size,
    new_users: users.filter((user) => time(user.created_at) >= startMs).length,
    corporate_users: users.filter((user) => user.account_type === "corporate")
      .length,
    individual_users: users.filter((user) => user.account_type !== "corporate")
      .length,
    total_enrollments: windowEnrollments.length,
    active_enrollments: windowEnrollments.filter(
      (row) => row.enrollment_status === "active",
    ).length,
    completed_enrollments: completed,
    completion_rate: windowEnrollments.length
      ? Math.round((completed / windowEnrollments.length) * 100)
      : 0,
    revenue_by_currency: revenue,
    prior_revenue_by_currency: summarizeCurrencyAmounts(prior),
    revenue_growth_by_currency: revenueGrowthByCurrency(
      revenue,
      summarizeCurrencyAmounts(prior),
    ),
    average_revenue_per_user_by_currency: divideTotals(
      revenue,
      activeUserIds.size,
    ),
    // Lesson-progress rows are per lesson, not per session.
    avg_minutes_per_lesson: windowProgress.length
      ? Math.round(totalMinutes / windowProgress.length)
      : 0,
    total_study_hours: Math.round(totalMinutes / 60),
    popular_certifications: [...certifications]
      .map(([standard, stats]) => ({
        iso_standard: standard,
        enrollments: stats.enrollments,
        completed: stats.completed,
        revenue_by_currency: summarizeCurrencyAmounts(stats.payments),
        completion_rate: stats.enrollments
          ? Math.round((stats.completed / stats.enrollments) * 100)
          : 0,
      }))
      .sort(
        (a, b) =>
          b.enrollments - a.enrollments ||
          a.iso_standard.localeCompare(b.iso_standard),
      )
      .slice(0, 10),
    payment_methods: [...methods]
      .map(([method, rows]) => ({
        method,
        count: rows.length,
        revenue_by_currency: summarizeCurrencyAmounts(rows),
        percentage: current.length
          ? Math.round((rows.length / current.length) * 100)
          : 0,
      }))
      .sort((a, b) => b.count - a.count || a.method.localeCompare(b.method)),
    geographic_distribution: [...regions]
      .map(([name, stats]) => ({
        region: name,
        users: stats.users.size,
        revenue_by_currency: summarizeCurrencyAmounts(stats.payments),
      }))
      .sort((a, b) => b.users - a.users || a.region.localeCompare(b.region)),
  };
}

// Six calendar months ending with the current one; revenue per currency.
export function buildMonthlyTrends({
  payments = [],
  users = [],
  enrollments = [],
  now = new Date(),
}) {
  const months = [];
  for (let i = 5; i >= 0; i--) {
    const monthStart = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
    const within = (value) => {
      const at = time(value);
      return at >= monthStart.getTime() && at < monthEnd.getTime();
    };
    const monthEnrollments = enrollments.filter((row) =>
      within(row.enrolled_at),
    );
    const completions = monthEnrollments.filter(
      (row) => row.enrollment_status === "completed",
    ).length;
    months.push({
      month: monthStart.toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
      }),
      revenue_by_currency: summarizeCurrencyAmounts(
        payments.filter(
          (payment) =>
            payment.payment_status === "completed" &&
            within(paymentDate(payment)),
        ),
      ),
      new_users: users.filter((user) => within(user.created_at)).length,
      enrollments: monthEnrollments.length,
      completions,
      completion_rate: monthEnrollments.length
        ? Math.round((completions / monthEnrollments.length) * 100)
        : 0,
    });
  }
  return months;
}

// Currencies present anywhere in the analytics payload, sorted by code.
export function analyticsCurrencies(data) {
  const currencies = new Set(Object.keys(data?.revenue_by_currency || {}));
  for (const month of data?.monthly_trends || []) {
    Object.keys(month.revenue_by_currency || {}).forEach((c) =>
      currencies.add(c),
    );
  }
  return [...currencies].sort();
}

// Projects per-currency rows onto one currency for single-series charts.
export function revenueIn(rows = [], currency) {
  return rows.map((row) => ({
    ...row,
    revenue: Number(row.revenue_by_currency?.[currency] || 0),
  }));
}

// Admin analytics activity feed label for a payment. Cart payments have no
// course_id, so they are not labelled "Course".
export function describePaymentActivity(payment) {
  if (payment.course?.title) return payment.course.title;
  const meta = payment.payment_gateway_response || {};
  let ids = meta.course_ids;
  if (typeof ids === "string") {
    try {
      ids = JSON.parse(ids);
    } catch {
      ids = null;
    }
  }
  const count =
    (Array.isArray(ids) ? ids.length : 0) ||
    Number(meta.cart_item_count || meta.course_count || 0);
  if (count > 1) return `${count} courses (cart)`;
  if (meta.is_cart_purchase) return "a cart purchase";
  return "a purchase (course not recorded)";
}

// SuperAdminTransactions commission label: a direct sale has no commission;
// an explicit 0% offering stays 0%.
export function commissionLabel(txn) {
  if (txn.sale_type === "direct") return null;
  return txn.commission_rate == null
    ? "Commission rate not recorded"
    : `${Number(txn.commission_rate)}% commission`;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Global Sales partner-ID filter: only a complete UUID is sent to the server.
export function partnerIdFilter(input) {
  const value = String(input || "").trim();
  if (!value) return { teamId: "", hint: "" };
  if (UUID.test(value)) return { teamId: value.toLowerCase(), hint: "" };
  return {
    teamId: null,
    hint: "Enter a complete partner ID (UUID) to filter.",
  };
}

// StudentProgressTable tabs: "active" means at least one course still in
// progress, including one not started yet (0%).
export function filterStudentsByTab(students, tab) {
  if (tab === "active") {
    return students.filter((student) =>
      student.courses.some((course) => course.status === "active"),
    );
  }
  if (tab === "completed")
    return students.filter((student) => student.completedCount > 0);
  return students;
}
